// ============================================================================
// ACTIVITY FORM MODAL
// ============================================================================
// Add / Edit form modal for the Activity master.
//
// Follows the prima-web mandatory form-modal blueprint (see CLAUDE.md):
//  - Internal formData state with refs for stable callbacks
//  - Initial snapshot for unsaved-changes (dirty) tracking
//  - Inline field validation via fieldErrors + getFieldError + clearFieldError
//  - 3-stage save flow: validate → ConfirmDialog → loading → toast
//  - Discard-changes ConfirmDialog on close when isDirty
//  - Loading overlay during save (uses var(--modal-loading-overlay))
//  - Minimize-to-bar support (chevronDown header button) — multi-instance Add
//
// Field set mirrors prima-api CreateActivityRequest:
//   code              [Required, max 30, uppercase]
//   name              [Required, max 150]
//   aliasName         [optional, max 100]
//   description       [optional]
//   chargeTypeEnumId  [Required, UUID FK]
//   engagementTypeIds [optional, UUID[] junction]
//   statusCode        ("ACTIVE" or "INACTIVE")

import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';

export interface ActivityFormData {
  code: string;
  name: string;
  chargeTypeEnumId: string;
  engagementTypeIds: string[];
  statusCode: 'ACTIVE' | 'INACTIVE';
}

export interface ActivityFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  initialData?: ActivityFormData | null;
  isSaving?: boolean;
  apiError?: string | null;
  onSave: (data: ActivityFormData) => void;
  onClose: () => void;
  // Lookup options passed from the screen
  chargeTypeOptions: { value: string; label: string }[];
  engagementTypeOptions: { value: string; label: string }[];
  /** Map of chargeTypeEnumId → chargeTypeKey (e.g. UUID → 'C') for conditional validation */
  chargeTypeKeyMap: Record<string, string>;
  // Minimize-to-bar support
  tabId?: string;
  moduleName?: string;
  onRestoreModal?: (mode: 'create' | 'edit', instanceId?: string, recordCode?: string) => void;
  createInstanceId?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
}

const INITIAL_FORM_DATA: ActivityFormData = {
  code: '',
  name: '',
  chargeTypeEnumId: '',
  engagementTypeIds: [],
  statusCode: 'ACTIVE',
};

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

const CODE_PATTERN = /^[A-Z0-9_-]+$/;

export const ActivityFormModal: React.FC<ActivityFormModalProps> = ({
  isOpen,
  mode,
  initialData,
  isSaving = false,
  apiError,
  onSave,
  onClose,
  chargeTypeOptions,
  engagementTypeOptions,
  chargeTypeKeyMap,
  tabId,
  moduleName,
  onRestoreModal,
  createInstanceId,
  minimizeRef,
}) => {
  const { minimize } = useModalMinimize();

  // --- Internal form state (Pattern B) ---
  const [formData, setFormData] = useState<ActivityFormData>(INITIAL_FORM_DATA);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const initialDataRef = useRef(initialData);
  initialDataRef.current = initialData;
  const createInstanceIdRef = useRef(createInstanceId);
  createInstanceIdRef.current = createInstanceId;
  const initialSnapshotRef = useRef('');
  const isRestoreRef = useRef(false);

  // --- Validation state ---
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const getFieldError = useCallback(
    (field: string) => fieldErrors[field],
    [fieldErrors]
  );
  const clearFieldError = useCallback((field: string) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  // --- Confirm dialog state ---
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  // --- Populate / reset form on open. Skip on restore. ---
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) {
      isRestoreRef.current = false;
      setFieldErrors({});
      return;
    }
    const src = initialDataRef.current;
    const data: ActivityFormData = src
      ? {
          code: src.code ?? '',
          name: src.name ?? '',
          chargeTypeEnumId: src.chargeTypeEnumId ?? '',
          engagementTypeIds: src.engagementTypeIds ?? [],
          statusCode: (src.statusCode as 'ACTIVE' | 'INACTIVE') ?? 'ACTIVE',
        }
      : { ...INITIAL_FORM_DATA };
    setFormData(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
  }, [isOpen]);

  // --- Dirty tracking ---
  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  // --- Close handler ---
  const handleClose = useCallback(() => {
    if (isSaving) return;
    if (isDirty) {
      setDiscardConfirmOpen(true);
    } else {
      onClose();
    }
  }, [isDirty, onClose, isSaving]);

  // --- Minimize handler ---
  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentFormData = formDataRef.current;
    const currentMode = modeRef.current;
    const currentCreateInstanceId = createInstanceIdRef.current;

    let modalId: string;
    if (currentMode === 'create') {
      modalId = `${tabId}-form-modal-create-${currentCreateInstanceId || 'default'}`;
    } else {
      modalId = `${tabId}-form-modal-edit-${currentFormData.code || currentFormData.name || 'new'}`;
    }

    let title: string;
    if (currentMode === 'create') {
      const name = currentFormData.name?.trim();
      const code = currentFormData.code?.trim();
      const idx = currentCreateInstanceId || '1';
      title = name ? `Add - ${name} (${idx})` : code ? `Add - ${code} (${idx})` : `Add New (${idx})`;
    } else {
      title = `Edit - ${currentFormData.name || currentFormData.code || 'Activity'}`;
    }

    const savedFormData = { ...currentFormData };
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot
      ? JSON.stringify(currentFormData) !== savedSnapshot
      : false;

    minimize({
      id: modalId,
      title,
      moduleName,
      tabId,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(savedFormData);
        initialSnapshotRef.current = savedSnapshot;
        if (onRestoreModal) {
          onRestoreModal(
            currentMode,
            currentMode === 'create' ? currentCreateInstanceId : undefined,
            currentMode !== 'create' ? currentFormData.code : undefined,
          );
        }
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  // --- Validate then show save confirmation ---
  const handleSave = useCallback(() => {
    const errors: Record<string, string> = {};
    const code = formData.code.trim();
    const name = formData.name.trim();

    // Code is optional but if provided must be valid
    if (code && code.length > 30) {
      errors.code = 'Code must be 30 characters or less';
    } else if (code && !CODE_PATTERN.test(code)) {
      errors.code = 'Code can only contain uppercase letters, numbers, hyphens, and underscores';
    }

    if (!name) {
      errors.name = 'Activity name is required';
    }

    if (!formData.chargeTypeEnumId) {
      errors.chargeTypeEnumId = 'Charge type is required';
    }

    // Engagement types required only when charge type is Chargeable (key = 'C')
    const selectedChargeKey = formData.chargeTypeEnumId
      ? chargeTypeKeyMap[formData.chargeTypeEnumId]
      : '';
    if (selectedChargeKey === 'C' && formData.engagementTypeIds.length === 0) {
      errors.engagementTypeIds = 'At least one engagement type is required for chargeable activities';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      requestAnimationFrame(() => {
        const firstField = Object.keys(errors)[0];
        const el = document.querySelector<HTMLElement>(
          `[data-field="${firstField}"]`
        );
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.querySelector<HTMLInputElement>('input, textarea')?.focus();
        }
      });
      return;
    }

    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData]);

  // --- Confirmed save → delegate to parent ---
  const handleConfirmSave = useCallback(() => {
    setSaveConfirmOpen(false);
    onSave({
      ...formData,
      code: formData.code.trim().toUpperCase(),
      name: formData.name.trim(),
    });
  }, [formData, onSave]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg">
        {/* Loading overlay */}
        {isSaving && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'var(--modal-loading-overlay)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              borderRadius: 'inherit',
              gap: '0.75rem',
            }}
          >
            <div
              style={{
                width: '2rem',
                height: '2rem',
                border: '3px solid var(--border-color)',
                borderTopColor: '#2563eb',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }}
            />
            <span
              style={{
                fontSize: '0.85rem',
                color: 'var(--text-secondary)',
                fontWeight: 500,
              }}
            >
              Saving...
            </span>
          </div>
        )}

        {/* Header */}
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            {mode === 'create' ? 'Add New Activity' : 'Edit Activity'}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button
                className="modal-header-btn"
                onClick={handleMinimize}
                title="Minimize to bar"
                disabled={isSaving}
              >
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button
              className="modal-close"
              onClick={handleClose}
              disabled={isSaving}
              title="Close"
            >
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="modal-content">
          {apiError && (
            <div
              style={{
                marginBottom: '0.75rem',
                padding: '0.625rem 0.75rem',
                background: 'rgba(220, 38, 38, 0.08)',
                border: '1px solid #dc2626',
                borderRadius: '4px',
                color: '#dc2626',
                fontSize: '0.8125rem',
                lineHeight: 1.4,
              }}
            >
              {apiError}
            </div>
          )}

          {/* Row 1: Code (optional) + Status */}
          <div className="form-grid-2">
            <div className="form-group" data-field="code">
              <label className="form-label">Code</label>
              <input
                type="text"
                className={`form-input${getFieldError('code') ? ' form-field-error' : ''}`}
                value={formData.code}
                maxLength={30}
                placeholder="Optional code"
                disabled={isSaving || mode === 'edit'}
                onChange={(e) => {
                  clearFieldError('code');
                  setFormData((prev) => ({ ...prev, code: e.target.value.toUpperCase() }));
                }}
              />
              {getFieldError('code') && (
                <div className="form-error-message">{getFieldError('code')}</div>
              )}
            </div>

            <div className="form-group" data-field="statusCode">
              <SyutSelect
                label="Status"
                value={formData.statusCode}
                options={STATUS_OPTIONS}
                isClearable={false}
                size="sm"
                onChange={(val) => {
                  setFormData((prev) => ({
                    ...prev,
                    statusCode: (val as 'ACTIVE' | 'INACTIVE') || 'ACTIVE',
                  }));
                }}
              />
            </div>
          </div>

          {/* Activity Name (long text — textarea, max 150 chars) */}
          <div className="form-group" data-field="name">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <label className="form-label">
                Activity Name <span className="text-danger">*</span>
              </label>
              <span style={{
                fontSize: '0.7rem',
                color: formData.name.length > 140
                  ? formData.name.length >= 150 ? 'var(--danger, #ef4444)' : 'var(--warning, #d97706)'
                  : 'var(--text-muted)',
                fontWeight: formData.name.length > 140 ? 600 : 400,
              }}>
                {formData.name.length}/150
              </span>
            </div>
            <textarea
              className={`form-input${getFieldError('name') ? ' form-field-error' : ''}`}
              value={formData.name}
              maxLength={150}
              rows={2}
              placeholder="e.g. Statutory Audit under Companies Act, 2013"
              disabled={isSaving}
              onChange={(e) => {
                clearFieldError('name');
                setFormData((prev) => ({ ...prev, name: e.target.value }));
              }}
              style={{ resize: 'vertical' }}
            />
            {getFieldError('name') && (
              <div className="form-error-message">{getFieldError('name')}</div>
            )}
          </div>

          {/* Charge Type */}
          <div className="form-group" data-field="chargeTypeEnumId">
            <SyutSelect
              label={<>Charge Type <span className="text-danger">*</span></>}
              value={formData.chargeTypeEnumId}
              options={chargeTypeOptions}
              isClearable={false}
              size="sm"
              error={getFieldError('chargeTypeEnumId')}
              onChange={(val) => {
                clearFieldError('chargeTypeEnumId');
                clearFieldError('engagementTypeIds');
                const newId = (val as string) || '';
                const newKey = newId ? chargeTypeKeyMap[newId] : '';
                setFormData((prev) => ({
                  ...prev,
                  chargeTypeEnumId: newId,
                  // Clear engagement types when switching away from Chargeable
                  engagementTypeIds: newKey === 'C' ? prev.engagementTypeIds : [],
                }));
              }}
            />
          </div>

          {/* Engagement Types (multi-select) — visible + required only when charge type is Chargeable */}
          {formData.chargeTypeEnumId && chargeTypeKeyMap[formData.chargeTypeEnumId] === 'C' && (
          <div className="form-group" data-field="engagementTypeIds">
            <SyutSelect
              label={<>Applicable Engagement Types <span className="text-danger">*</span></>}
              value={formData.engagementTypeIds}
              options={engagementTypeOptions}
              isMulti
              isClearable
              size="sm"
              placeholder="Select engagement types..."
              error={getFieldError('engagementTypeIds')}
              onChange={(val) => {
                clearFieldError('engagementTypeIds');
                setFormData((prev) => ({
                  ...prev,
                  engagementTypeIds: (val as string[]) || [],
                }));
              }}
            />
          </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button
            className="btn btn-secondary"
            onClick={handleClose}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={isSaving}
          >
            {mode === 'create' ? 'Create' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Save confirmation */}
      <ConfirmDialog
        open={saveConfirmOpen}
        title={mode === 'create' ? 'Confirm Create' : 'Confirm Update'}
        message={
          mode === 'create'
            ? `Create activity "${formData.name.trim()}"?`
            : `Save changes to "${formData.name.trim()}"?`
        }
        confirmLabel={mode === 'create' ? 'Create' : 'Save'}
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      {/* Discard changes confirmation */}
      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => {
          setDiscardConfirmOpen(false);
          onClose();
        }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
};

export default ActivityFormModal;
